import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete, Edit, History, Replay, Send, Webhook } from '@mui/icons-material'

import {
    useCreateDestinationMutation,
    useDeleteDestinationMutation,
    useGetDeliveriesQuery,
    useGetProjectConfigQuery,
    useListDestinationsQuery,
    useRedeliverMutation,
    useTestDestinationMutation,
    useUpdateDestinationMutation,
} from '@/apis/v2Api'
import {
    DESTINATION_TYPES,
    EMPTY_DESTINATION_FORM,
    destinationFormReady,
    destinationInput,
    destinationTestMessage,
    isAdDestination,
    v2ErrorMessage,
    type AdDestinationKind,
    type Destination,
    type DestinationForm,
    type DestinationType,
} from '@/apis/v2Mappers'
import { useAppDispatch } from '@/app/hooks'
import { showNotification } from '@/app/notificationSlice'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { EmptyState } from '@/components/shared/EmptyState'
import { useProjectId } from '@/hooks/useReportArgs'

import { ReportHeader, ReportStatus, fmtDateTime, fmtNum } from './common'
import { EditAdDestinationDialog } from './EditAdDestinationDialog'

/** What a destination sends to: the URL, or the ad-platform id plus the masked credential. */
function destinationTarget(d: Destination): string {
    if (d.type === 'meta_capi') {
        return `Meta pixel ${d.config?.pixel_id ?? ''} · token ${d.credential?.hint ?? ''}`
    }
    if (d.type === 'ga4_mp') {
        return `GA4 ${d.config?.measurement_id ?? ''} · secret ${d.credential?.hint ?? ''}`
    }
    return d.url
}

function DeliveriesDialog({
    destination,
    onClose,
}: {
    destination: Destination
    onClose: () => void
}) {
    const projectId = useProjectId()
    const { data, isLoading, error, refetch } = useGetDeliveriesQuery({
        projectId,
        id: destination.id,
    })
    const [redeliver, redeliverState] = useRedeliverMutation()
    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                Delivery log · {destination.name || destinationTarget(destination)}
            </DialogTitle>
            <DialogContent>
                {data?.health && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Health:{' '}
                        {Object.entries(data.health)
                            .filter(([k]) => !['project_id', 'destination_id'].includes(k))
                            .map(
                                ([k, v]) =>
                                    `${k.replace(/_/g, ' ')}: ${
                                        v === null
                                            ? '—'
                                            : k.endsWith('_at') || k.endsWith('_since')
                                              ? fmtDateTime(Number(v))
                                              : String(v)
                                    }`,
                            )
                            .join(' · ')}
                    </Alert>
                )}
                {redeliverState.error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {v2ErrorMessage(redeliverState.error)}
                    </Alert>
                )}
                <ReportStatus
                    isLoading={isLoading}
                    error={error}
                    isEmpty={!!data && data.deliveries.length === 0}
                    emptyIcon={<History />}
                    emptyTitle="No deliveries yet"
                    emptyDescription="Send a test event to check the endpoint."
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Time</TableCell>
                                <TableCell>Kind</TableCell>
                                <TableCell>Event</TableCell>
                                <TableCell align="right">Events</TableCell>
                                <TableCell>Attempt</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>HTTP</TableCell>
                                <TableCell align="right">Duration</TableCell>
                                <TableCell>Error / response</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(data?.deliveries ?? []).map(d => (
                                <TableRow key={d.id} hover>
                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                        {fmtDateTime(Number(d.created_at))}
                                    </TableCell>
                                    <TableCell>
                                        {d.kind}
                                        {d.manual ? ' (manual)' : ''}
                                    </TableCell>
                                    <TableCell>{d.event_type}</TableCell>
                                    <TableCell align="right">{fmtNum(d.event_count)}</TableCell>
                                    <TableCell>{d.attempt}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={d.status}
                                            color={d.status === 'delivered' ? 'success' : 'error'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{d.http_status ?? '—'}</TableCell>
                                    <TableCell align="right">
                                        {d.duration_ms === null ? '—' : `${d.duration_ms} ms`}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 260 }}>
                                        <Typography variant="caption" noWrap display="block">
                                            {d.error ?? d.response_snippet ?? ''}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {d.redeliverable ? (
                                            <Tooltip title="Redeliver now">
                                                <IconButton
                                                    size="small"
                                                    aria-label="Redeliver now"
                                                    disabled={redeliverState.isLoading}
                                                    onClick={() => {
                                                        void redeliver({
                                                            projectId,
                                                            deliveryId: d.id,
                                                        })
                                                    }}
                                                >
                                                    <Replay fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ReportStatus>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        void refetch()
                    }}
                >
                    Refresh
                </Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    )
}

/** v2 `/destinations` (signed webhooks, Meta CAPI, GA4 MP) — list, create, edit (ad platforms), toggle, test, delete, delivery log. */
export function DestinationsPage() {
    const projectId = useProjectId()
    const dispatch = useAppDispatch()
    const { data = [], isLoading, error } = useListDestinationsQuery(projectId)
    const { data: cfg } = useGetProjectConfigQuery(projectId)
    const [createDestination, createState] = useCreateDestinationMutation()
    const [updateDestination] = useUpdateDestinationMutation()
    const [deleteDestination] = useDeleteDestinationMutation()
    const [testDestination, testState] = useTestDestinationMutation()

    const [open, setOpen] = useState(false)
    const [form, setForm] = useState<DestinationForm>(EMPTY_DESTINATION_FORM)
    const [secret, setSecret] = useState<string | null>(null)
    const [logFor, setLogFor] = useState<Destination | null>(null)
    const [toDelete, setToDelete] = useState<Destination | null>(null)
    const [toEdit, setToEdit] = useState<(Destination & { type: AdDestinationKind }) | null>(null)
    const realtimeAllowed = cfg?.entitlements.realtimeDestinations ?? false
    const isAd = form.type !== 'webhook'
    const set = (k: keyof DestinationForm) => (e: { target: { value: string } }) =>
        setForm({ ...form, [k]: e.target.value })

    const create = async () => {
        try {
            const d = await createDestination({ projectId, ...destinationInput(form) }).unwrap()
            setSecret(d.secret ?? null)
            setOpen(false)
            setForm(EMPTY_DESTINATION_FORM)
        } catch {
            // shown from createState.error
        }
    }

    const test = async (d: Destination) => {
        try {
            const r = await testDestination({ projectId, id: d.id }).unwrap()
            dispatch(
                showNotification({
                    message: destinationTestMessage(r),
                    severity: r.ok ? 'success' : 'error',
                }),
            )
        } catch (err) {
            dispatch(
                showNotification({
                    message: v2ErrorMessage(err) ?? 'Test failed',
                    severity: 'error',
                }),
            )
        }
    }

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Destinations"
                subtitle="Signed webhooks and ad platforms (Meta Conversions API, GA4) that receive events as they are collected or in batches. Ad platforms only get events with marketing consent and no GPC."
                actions={
                    <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}>
                        New destination
                    </Button>
                }
            />
            {secret && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSecret(null)}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        Signing secret (shown once — store it to verify the signature header):
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontFamily: 'monospace',
                        }}
                    >
                        {secret}
                        <CopyButton text={secret} />
                    </Box>
                </Alert>
            )}
            <ReportStatus isLoading={isLoading} error={error}>
                {data.length === 0 ? (
                    <EmptyState
                        icon={<Webhook />}
                        title="No destinations yet"
                        description="Forward events to your own backend or CRM with signed webhooks, or conversions to Meta and GA4 server-side."
                        actionLabel="Add destination"
                        onAction={() => setOpen(true)}
                    />
                ) : (
                    <Card>
                        <CardContent>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name / target</TableCell>
                                        <TableCell>Events</TableCell>
                                        <TableCell>Mode</TableCell>
                                        <TableCell>Sub-tenant</TableCell>
                                        <TableCell>Active</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map(d => (
                                        <TableRow key={d.id} hover>
                                            <TableCell sx={{ maxWidth: 340 }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {d.name || '(unnamed)'}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    noWrap
                                                    display="block"
                                                    title={destinationTarget(d)}
                                                >
                                                    {destinationTarget(d)}
                                                </Typography>
                                                {d.disabled_reason && (
                                                    <Typography variant="caption" color="error">
                                                        Disabled: {d.disabled_reason}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {d.events.map(e => (
                                                    <Chip
                                                        key={e}
                                                        size="small"
                                                        label={e}
                                                        sx={{ mr: 0.5, mb: 0.5 }}
                                                    />
                                                ))}
                                            </TableCell>
                                            <TableCell>{d.mode}</TableCell>
                                            <TableCell>{d.sub_tenant ?? 'all'}</TableCell>
                                            <TableCell>
                                                <Switch
                                                    size="small"
                                                    checked={d.active}
                                                    onChange={e => {
                                                        void updateDestination({
                                                            projectId,
                                                            id: d.id,
                                                            active: e.target.checked,
                                                        })
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title="Send test event">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Send test event to ${d.name || 'destination'}`}
                                                            disabled={testState.isLoading}
                                                            onClick={() => {
                                                                void test(d)
                                                            }}
                                                        >
                                                            <Send fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                {isAdDestination(d) && (
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            aria-label={`Edit ${d.name || 'destination'}`}
                                                            onClick={() => setToEdit(d)}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delivery log">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setLogFor(d)}
                                                    >
                                                        <History fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setToDelete(d)}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </ReportStatus>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New destination</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {createState.error && (
                        <Alert severity="error">{v2ErrorMessage(createState.error)}</Alert>
                    )}
                    <TextField
                        size="small"
                        select
                        label="Type"
                        value={form.type}
                        onChange={e =>
                            setForm({ ...form, type: e.target.value as DestinationType })
                        }
                        sx={{ mt: 1 }}
                    >
                        {DESTINATION_TYPES.map(t => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    {isAd && (
                        <Alert severity="info">
                            Sends purchase, generate_lead, sign_up, add_to_cart, begin_checkout and
                            view_item with the browser event id, so the platform deduplicates them
                            against its pixel. Needs a paid plan.
                        </Alert>
                    )}
                    <TextField size="small" label="Name" value={form.name} onChange={set('name')} />
                    {form.type === 'webhook' && (
                        <>
                            <TextField
                                size="small"
                                label="Endpoint URL"
                                required
                                placeholder="https://example.com/webhooks/yom"
                                value={form.url}
                                onChange={set('url')}
                            />
                            <TextField
                                size="small"
                                label="Events"
                                helperText='Comma separated: "*" (all), "event.*" or "event.<name>"'
                                value={form.events}
                                onChange={set('events')}
                            />
                        </>
                    )}
                    {form.type === 'meta_capi' && (
                        <>
                            <TextField
                                size="small"
                                label="Pixel / dataset id"
                                required
                                value={form.pixelId}
                                onChange={set('pixelId')}
                            />
                            <TextField
                                size="small"
                                label="Conversions API access token"
                                required
                                type="password"
                                autoComplete="off"
                                helperText="Stored encrypted and never shown again."
                                value={form.accessToken}
                                onChange={set('accessToken')}
                            />
                            <TextField
                                size="small"
                                label="Test event code (optional)"
                                helperText="From Events Manager → Test events, while you verify the setup."
                                value={form.testEventCode}
                                onChange={set('testEventCode')}
                            />
                        </>
                    )}
                    {form.type === 'ga4_mp' && (
                        <>
                            <TextField
                                size="small"
                                label="Measurement id"
                                required
                                placeholder="G-XXXXXXX"
                                value={form.measurementId}
                                onChange={set('measurementId')}
                            />
                            <TextField
                                size="small"
                                label="Measurement Protocol API secret"
                                required
                                type="password"
                                autoComplete="off"
                                helperText="GA4 Admin → Data streams → Measurement Protocol API secrets."
                                value={form.apiSecret}
                                onChange={set('apiSecret')}
                            />
                        </>
                    )}
                    <TextField
                        size="small"
                        select
                        label="Mode"
                        value={form.mode}
                        onChange={e =>
                            setForm({ ...form, mode: e.target.value as DestinationForm['mode'] })
                        }
                    >
                        <MenuItem value="batched">Batched</MenuItem>
                        <MenuItem value="realtime" disabled={!realtimeAllowed}>
                            Realtime{realtimeAllowed ? '' : ' (paid plans)'}
                        </MenuItem>
                    </TextField>
                    <TextField
                        size="small"
                        label="Only this sub-tenant (optional)"
                        value={form.subTenant}
                        onChange={set('subTenant')}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!destinationFormReady(form) || createState.isLoading}
                        onClick={() => {
                            void create()
                        }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>

            {logFor && <DeliveriesDialog destination={logFor} onClose={() => setLogFor(null)} />}

            {toEdit && (
                <EditAdDestinationDialog
                    projectId={projectId}
                    destination={toEdit}
                    realtimeAllowed={realtimeAllowed}
                    onClose={() => setToEdit(null)}
                />
            )}

            <ConfirmDialog
                open={!!toDelete}
                title="Delete destination?"
                message={`Deliveries to ${toDelete ? destinationTarget(toDelete) : ''} stop immediately.`}
                confirmLabel="Delete"
                confirmColor="error"
                onConfirm={() => {
                    if (toDelete) {
                        void deleteDestination({ projectId, id: toDelete.id })
                    }
                    setToDelete(null)
                }}
                onCancel={() => setToDelete(null)}
            />
        </Box>
    )
}
